import { useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";

const ByLaws = () => {
  const [currentPage, setCurrentPage] = useState(1);

  // Bylaws page images - CDN URLs
  const bylawsPages = [
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/kMyAVhBftHkEefCX.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/mWgSdVVXHfTseeGM.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/uVxZIDCMAcURliNx.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/OuIBaEWVNuPrLCct.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/SYxcQKifMbOcqWpr.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/njopklFIYDjXayVA.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/XudTpPozQMbSblyw.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/pdSVpoKAgMDaBioa.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/VjDNAaiUPVFKFDqZ.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/tmktdOwTeDYEAypF.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/ShAAMhtFolKKuTKL.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/kHeMEgJulGiBepeT.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/GJAGQPwFOkPklKNs.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/YgRVhAKxxvxjuDaM.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/guXozzHxxvuAVCkn.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/fGzbuyRvmxHIZqaO.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/hzqZmGRqeINgZyjh.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/mnyJfGxNkEgZIVHX.png",
    "https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/dZgzpXyLDPOrhrJA.png",
  ];

  const totalPages = bylawsPages.length;

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="section-container py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">PSAOA By-Laws</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Official By-Laws of Purva Swanlake Apartment Owners' Association
              </p>
            </div>
            <a
              href="https://files.manuscdn.com/user_upload_by_module/session_file/310519663035723669/muYyzZhBDJMeSOiO.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
          </div>

          {/* Page Navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className="p-2 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium text-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="p-2 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Page Jump */}
            <div className="flex items-center gap-2">
              <label htmlFor="page-select" className="text-sm text-muted-foreground">
                Go to page:
              </label>
              <select
                id="page-select"
                value={currentPage}
                onChange={(e) => goToPage(Number(e.target.value))}
                className="px-3 py-1 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <option key={page} value={page}>
                    {page}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="section-container py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Page Display */}
          <div className="flex justify-center bg-slate-100 p-4">
            <img
              src={bylawsPages[currentPage - 1]}
              alt={`By-Laws Page ${currentPage}`}
              className="max-w-full h-auto shadow-md rounded"
            />
          </div>

          {/* Bottom Navigation */}
          <div className="bg-white border-t border-slate-200 p-6 flex items-center justify-between">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>

            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Page <span className="font-semibold text-foreground">{currentPage}</span> of{" "}
                <span className="font-semibold text-foreground">{totalPages}</span>
              </p>
            </div>

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Page Thumbnails */}
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-foreground mb-4">Jump to Page</h3>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
            {bylawsPages.map((_, index) => (
              <button
                key={index + 1}
                onClick={() => goToPage(index + 1)}
                className={`aspect-square rounded-lg text-xs font-medium transition-all ${
                  currentPage === index + 1
                    ? "bg-blue-600 text-white ring-2 ring-blue-400"
                    : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ByLaws;
